package design

import org.jetbrains.compose.web.css.*
import kotlinx.browser.document
import kotlinx.browser.window

/**
 * Theme System for Schneeblume
 *
 * Provides light and dark theme support with automatic theme detection
 * and manual theme switching capabilities.
 *
 * Theme modes:
 * - LIGHT: Always use light theme
 * - DARK: Always use dark theme
 * - SYSTEM: Automatically detect system preference
 */
object Theme {
    /**
     * Theme mode enumeration
     */
    enum class ThemeMode {
        LIGHT,
        DARK,
        SYSTEM
    }

    /**
     * Color palette data class
     *
     * Contains all semantic colors for a theme mode
     */
    data class ColorPalette(
        // Primary colors
        val primary: String,
        val primaryDark: String,
        val primaryLight: String,
        val secondary: String,

        // Semantic colors
        val success: String,
        val warning: String,
        val danger: String,
        val info: String,

        // Neutral colors
        val gray50: String,
        val gray100: String,
        val gray200: String,
        val gray300: String,
        val gray400: String,
        val gray500: String,
        val gray600: String,
        val gray700: String,
        val gray800: String,
        val gray900: String,

        // Semantic colors (theme-specific)
        val background: String,
        val surface: String,
        val text: String,
        val textSecondary: String,
        val textMuted: String,
        val border: String,
        val focus: String,
        val focusRing: String,
        val white: String
    ) {
        companion object {
            /**
             * Light theme color palette
             */
            val lightPalette = ColorPalette(
                // Primary colors
                primary = "#4A90E2",
                primaryDark = "#357ABD",
                primaryLight = "#6AB0FF",
                secondary = "#7B68EE",

                // Semantic colors
                success = "#28A745",
                warning = "#FFC107",
                danger = "#DC3545",
                info = "#17A2B8",

                // Neutral colors
                gray50 = "#F9FAFB",
                gray100 = "#F3F4F6",
                gray200 = "#E5E7EB",
                gray300 = "#D1D5DB",
                gray400 = "#9CA3AF",
                gray500 = "#6B7280",
                gray600 = "#4B5563",
                gray700 = "#374151",
                gray800 = "#1F2937",
                gray900 = "#111827",

                // Semantic colors (light mode)
                background = "#FFFFFF",
                surface = "#F9FAFB",
                text = "#111827",
                textSecondary = "#6B7280",
                textMuted = "#9CA3AF",
                border = "#E5E7EB",
                focus = "#4A90E2",
                focusRing = "rgba(74, 144, 226, 0.25)",
                white = "#FFFFFF"
            )

            /**
             * Dark theme color palette
             */
            val darkPalette = ColorPalette(
                // Primary colors (lighter for dark mode)
                primary = "#6AB0FF",
                primaryDark = "#4A90E2",
                primaryLight = "#8AC0FF",
                secondary = "#9D8FF0",

                // Semantic colors
                success = "#22C55E",
                warning = "#FBBF24",
                danger = "#F87171",
                info = "#22D3EE",

                // Neutral colors (inverted for dark mode)
                gray50 = "#111827",
                gray100 = "#1F2937",
                gray200 = "#374151",
                gray300 = "#4B5563",
                gray400 = "#6B7280",
                gray500 = "#9CA3AF",
                gray600 = "#D1D5DB",
                gray700 = "#E5E7EB",
                gray800 = "#F3F4F6",
                gray900 = "#F9FAFB",

                // Semantic colors (dark mode)
                background = "#111827",
                surface = "#1F2937",
                text = "#F9FAFB",
                textSecondary = "#D1D5DB",
                textMuted = "#9CA3AF",
                border = "#374151",
                focus = "#6AB0FF",
                focusRing = "rgba(106, 176, 255, 0.25)",
                white = "#FFFFFF"
            )
        }
    }

    /**
     * Current theme mode (default: SYSTEM)
     */
    private var currentMode: ThemeMode = ThemeMode.SYSTEM

    /**
     * Get the current theme mode
     */
    fun getThemeMode(): ThemeMode = currentMode

    /**
     * Set the theme mode
     *
     * @param mode The theme mode to set
     */
    fun setThemeMode(mode: ThemeMode) {
        currentMode = mode
        applyTheme(mode)
        saveThemePreference(mode)
    }

    /**
     * Detect system theme preference
     *
     * @return true if dark mode is preferred, false otherwise
     */
    private fun detectSystemTheme(): Boolean {
        return window.matchMedia("(prefers-color-scheme: dark)").matches
    }

    /**
     * Get the color palette for a given theme mode
     *
     * @param mode The theme mode
     * @return The color palette for the theme
     */
    fun getPalette(mode: ThemeMode = currentMode): ColorPalette {
        return when (mode) {
            ThemeMode.LIGHT -> ColorPalette.lightPalette
            ThemeMode.DARK -> ColorPalette.darkPalette
            ThemeMode.SYSTEM -> if (detectSystemTheme()) {
                ColorPalette.darkPalette
            } else {
                ColorPalette.lightPalette
            }
        }
    }

    /**
     * Apply theme to the document
     *
     * Sets CSS custom properties for all colors
     *
     * @param mode The theme mode to apply
     */
    fun applyTheme(mode: ThemeMode = currentMode) {
        val palette = getPalette(mode)
        val root = document.documentElement?.asDynamic()

        // Primary colors
        root?.style?.setProperty("--primary-color", palette.primary)
        root?.style?.setProperty("--primary-dark", palette.primaryDark)
        root?.style?.setProperty("--primary-light", palette.primaryLight)
        root?.style?.setProperty("--secondary-color", palette.secondary)

        // Semantic colors
        root?.style?.setProperty("--success-color", palette.success)
        root?.style?.setProperty("--warning-color", palette.warning)
        root?.style?.setProperty("--danger-color", palette.danger)
        root?.style?.setProperty("--info-color", palette.info)

        // Neutral colors
        root?.style?.setProperty("--gray-50", palette.gray50)
        root?.style?.setProperty("--gray-100", palette.gray100)
        root?.style?.setProperty("--gray-200", palette.gray200)
        root?.style?.setProperty("--gray-300", palette.gray300)
        root?.style?.setProperty("--gray-400", palette.gray400)
        root?.style?.setProperty("--gray-500", palette.gray500)
        root?.style?.setProperty("--gray-600", palette.gray600)
        root?.style?.setProperty("--gray-700", palette.gray700)
        root?.style?.setProperty("--gray-800", palette.gray800)
        root?.style?.setProperty("--gray-900", palette.gray900)

        // Semantic colors (theme-specific)
        root?.style?.setProperty("--bg-color", palette.background)
        root?.style?.setProperty("--surface-color", palette.surface)
        root?.style?.setProperty("--text-color", palette.text)
        root?.style?.setProperty("--text-secondary", palette.textSecondary)
        root?.style?.setProperty("--text-muted", palette.textMuted)
        root?.style?.setProperty("--border-color", palette.border)
        root?.style?.setProperty("--focus-color", palette.focus)
        root?.style?.setProperty("--focus-ring", palette.focusRing)
        root?.style?.setProperty("--white", palette.white)

        // Set data attribute for CSS selectors
        document.documentElement?.setAttribute("data-theme", when (mode) {
            ThemeMode.LIGHT -> "light"
            ThemeMode.DARK -> "dark"
            ThemeMode.SYSTEM -> if (detectSystemTheme()) "dark" else "light"
        })
    }

    /**
     * Toggle between light and dark mode
     *
     * If current mode is SYSTEM, switches to LIGHT, then toggles between LIGHT and DARK
     */
    fun toggleTheme() {
        currentMode = when (currentMode) {
            ThemeMode.LIGHT -> ThemeMode.DARK
            ThemeMode.DARK -> ThemeMode.LIGHT
            ThemeMode.SYSTEM -> if (detectSystemTheme()) ThemeMode.LIGHT else ThemeMode.DARK
        }
        applyTheme(currentMode)
        saveThemePreference(currentMode)
    }

    /**
     * Save theme preference to localStorage
     *
     * @param mode The theme mode to save
     */
    private fun saveThemePreference(mode: ThemeMode) {
        try {
            window.localStorage.setItem("theme-mode", mode.name)
        } catch (e: Exception) {
            js("console.warn('Failed to save theme preference', e)")
        }
    }

    /**
     * Load theme preference from localStorage
     *
     * @return The saved theme mode, or SYSTEM if not found
     */
    fun loadThemePreference(): ThemeMode {
        return try {
            val saved = window.localStorage.getItem("theme-mode")
            when (saved) {
                "LIGHT" -> ThemeMode.LIGHT
                "DARK" -> ThemeMode.DARK
                "SYSTEM" -> ThemeMode.SYSTEM
                else -> ThemeMode.SYSTEM
            }
        } catch (e: Exception) {
            js("console.warn('Failed to load theme preference', e)")
            ThemeMode.SYSTEM
        }
    }

    /**
     * Initialize theme system
     *
     * Loads saved preference and applies theme
     */
    fun init() {
        currentMode = loadThemePreference()
        applyTheme(currentMode)

        // Listen for system theme changes
        window.matchMedia("(prefers-color-scheme: dark)")
            .addEventListener("change", { _ ->
                if (currentMode == ThemeMode.SYSTEM) {
                    applyTheme(ThemeMode.SYSTEM)
                }
            })
    }

    /**
     * Get a color value from the current theme
     *
     * @param colorName The CSS custom property name (without --)
     * @return The color value
     */
    fun getColor(colorName: String): String {
        return DesignTokens.Utils.varColor(colorName).toString()
    }

    /**
     * Check if dark mode is currently active
     *
     * @return true if dark mode is active
     */
    fun isDarkMode(): Boolean {
        return when (currentMode) {
            ThemeMode.DARK -> true
            ThemeMode.LIGHT -> false
            ThemeMode.SYSTEM -> detectSystemTheme()
        }
    }

    /**
     * Get CSS color value for a design token
     *
     * @param color The color from DesignTokens.Colors
     * @return CSS color value string
     */
    fun cssColor(color: Color): String = color.toString()

    /**
     * Get a reactive color value that updates with theme changes
     *
     * @param lightColor Color for light mode
     * @param darkColor Color for dark mode
     * @return CSS custom property reference
     */
    fun adaptiveColor(lightColor: String, darkColor: String): String {
        return "var(--adaptive-${lightColor.replace("#", "")}-${darkColor.replace("#", "")}, ${if (isDarkMode()) darkColor else lightColor})"
    }
}
